import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    MapPin,
    Bed,
    Bath,
    Square,
    Phone,
    Mail,
    X,
    Download,
    Share2,
    Home,
    ExternalLink,
    Loader2,
    User,
    Check,
    Play,
    Pause,
    MessageCircle
} from "lucide-react";
import { getPropertyGalleryTemplate } from "../config/propertyGalleryTemplates";
import MoreFromAgent from "./MoreFromAgent";

/**
 * Generate a URL-friendly slug from a property address
 * @param {string} address - Property address (e.g., "1031 Miller Ave, San Jose, CA 95129")
 * @returns {string} - URL slug (e.g., "1031-miller-ave-san-jose-ca-95129")
 */
const generateSlugFromAddress = (address) => {
    if (!address) return null;
    return address
        .toLowerCase()
        .replace(/[,\.#]/g, '')           // Remove commas, periods, hash
        .replace(/[^a-z0-9\s-]/g, '')     // Remove special characters
        .replace(/\s+/g, '-')              // Replace spaces with hyphens
        .replace(/-+/g, '-')               // Collapse multiple hyphens
        .replace(/^-|-$/g, '');            // Trim leading/trailing hyphens
};

/**
 * Fix over-encoded S3 URLs (e.g., %252540 → %2540 → %40 → @)
 * Backend sometimes double or triple encodes URLs
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== 'string') return url;
    let decoded = url;
    let prev = '';
    while (decoded !== prev && decoded.includes('%25')) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

/**
 * GalleryMetaTags - Open Graph and Twitter meta tags for social sharing
 * Note: For social media crawlers (Facebook, LinkedIn, iMessage, Slack) to see these tags,
 * you need a prerender service (e.g., prerender.io) since this is a client-side rendered app.
 */
const GalleryMetaTags = ({ gallery, galleryId }) => {
    // Defaults for when gallery data isn't available
    const defaultTitle = "Property Gallery | CloudMortgage.io";
    const defaultDescription = "View this beautifully staged property gallery.";
    const defaultImage = "https://cloudmortgage.io/default-gallery-og.jpg";

    // Extract data from gallery
    const title = gallery?.property?.address || gallery?.property?.name || defaultTitle;
    const description = gallery?.property?.description
        ? gallery.property.description.slice(0, 160)
        : defaultDescription;
    const image = gallery?.photos?.[0]?.processed || gallery?.photos?.[0]?.original || defaultImage;

    // Use slug for SEO-friendly URLs, fallback to galleryId
    const urlSlug = gallery?.slug || generateSlugFromAddress(gallery?.property?.address) || galleryId;
    const url = `https://cloudmortgage.io/flow/${urlSlug}`;

    return (
        <Helmet>
            {/* Basic SEO */}
            <title>{title} | CloudMortgage.io</title>
            <meta name="description" content={description} />

            {/* Open Graph (Facebook, LinkedIn, iMessage, Slack) */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="CloudMortgage.io" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
        </Helmet>
    );
};

// Sample gallery data for testing
const SAMPLE_GALLERY = {
    id: "smart-batch-295c1fc01c64",
    template: "luxury-dark",
    property: {
        name: "123 Main Street",
        address: "123 Main Street, Beverly Hills, CA 90210",
        price: 2495000,
        bedrooms: 4,
        bathrooms: 3,
        squareFeet: 3200,
        description: "Stunning modern home with breathtaking views and luxurious finishes throughout. This exquisite property features an open floor plan, gourmet kitchen, and resort-style backyard perfect for entertaining.",
        features: ["Pool", "Mountain Views", "Smart Home", "Wine Cellar"]
    },
    agent: {
        name: "Sarah Johnson",
        title: "Luxury Real Estate Specialist",
        phone: "(310) 555-0123",
        email: "sarah@luxuryrealty.com",
        photo: null,
        brokerage: "Luxury Realty Group",
        brokerageLogo: null
    },
    settings: {
        accentColor: "#d6b27f",
        showPrice: true,
        showContactForm: true,
        showDownloadButton: true
    },
    photos: [
        {
            id: 1,
            original: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200",
            processed: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200",
            room: "Living Room",
            style: "Modern"
        },
        {
            id: 2,
            original: "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=1200",
            processed: "https://images.unsplash.com/photo-1600607687644-aac4c3eac7f4?w=1200",
            room: "Kitchen",
            style: "Contemporary"
        },
        {
            id: 3,
            original: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200",
            processed: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=1200",
            room: "Master Bedroom",
            style: "Minimalist"
        },
        {
            id: 4,
            original: "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=1200",
            processed: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200",
            room: "Bathroom",
            style: "Spa"
        },
        {
            id: 5,
            original: "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=1200",
            processed: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1200",
            room: "Exterior",
            style: "Modern"
        },
        {
            id: 6,
            original: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200",
            processed: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=1200",
            room: "Backyard",
            style: "Resort"
        }
    ]
};

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
    })
};

const PropertyGalleryPage = ({ galleryId }) => {
    const [gallery, setGallery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);
    const [isSlideshowPlaying, setIsSlideshowPlaying] = useState(true);
    const [showContactForm, setShowContactForm] = useState(false);
    const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
    const [contactForm, setContactForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: ""
    });
    const [requestInfoForm, setRequestInfoForm] = useState({
        name: "",
        phone: "",
        email: "",
        message: "",
        wantFinancing: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [requestInfoSubmitted, setRequestInfoSubmitted] = useState(false);
    const [agentListings, setAgentListings] = useState(null);

    useEffect(() => {
        const fetchGallery = async () => {
            setLoading(true);

            try {
                const apiUrl = process.env.REACT_APP_API_BASE || (
                    window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://api.cloudmortgage.io'
                );

                // Determine if galleryId looks like a slug (address-based) or batch ID
                // Slugs are lowercase with hyphens (e.g., "1031-miller-ave-san-jose-ca-95129")
                // Batch IDs contain "smart-batch-" prefix (e.g., "smart-batch-6cdcfc317a4e")
                const isSlug = !galleryId.includes('smart-batch-') && !galleryId.includes('batch-');

                let response;

                if (isSlug) {
                    // Try slug-based endpoint first (new SEO-friendly URLs)
                    console.log('[PropertyGalleryPage] Trying slug endpoint:', `${apiUrl}/api/public/galleries/by-slug/${galleryId}`);
                    response = await fetch(`${apiUrl}/api/public/galleries/by-slug/${galleryId}`);

                    // If slug endpoint returns 404, try direct ID lookup as fallback
                    if (!response.ok && response.status === 404) {
                        console.log('[PropertyGalleryPage] Slug not found, trying direct ID lookup');
                        response = await fetch(`${apiUrl}/api/public/galleries/${galleryId}`);
                    }
                } else {
                    // Use direct ID lookup for batch IDs (backward compatibility)
                    console.log('[PropertyGalleryPage] Using direct ID lookup:', `${apiUrl}/api/public/galleries/${galleryId}`);
                    response = await fetch(`${apiUrl}/api/public/galleries/${galleryId}`);
                }

                console.log('[PropertyGalleryPage] Response status:', response.status);
                if (response.ok) {
                    const data = await response.json();
                    console.log('[PropertyGalleryPage] API data received:', data);
                    console.log('[PropertyGalleryPage] Agent photoUrl:', data.agent?.photoUrl);
                    console.log('[PropertyGalleryPage] Property description:', data.property?.description?.substring(0, 50));
                    // Transform API response to match component's expected format
                    // API returns snake_case fields, component expects camelCase
                    const transformedGallery = {
                        id: data.id,
                        batchId: data.batchId || data.batch_id,
                        slug: data.slug, // SEO-friendly URL slug (e.g., "1031-miller-ave-san-jose-ca-95129")
                        template: data.template,
                        property: {
                            name: data.property?.address || "Property Gallery",
                            address: data.property?.address,
                            price: data.property?.price ? parseInt(String(data.property.price).replace(/[^0-9]/g, '')) : null,
                            bedrooms: data.property?.bedrooms,
                            bathrooms: data.property?.bathrooms,
                            squareFeet: data.property?.squareFeet || data.property?.square_feet,
                            description: data.property?.description,
                            features: data.property?.features || []
                        },
                        agent: {
                            name: data.agent?.name || "Andrew Nguyen",
                            slug: data.agent?.slug, // URL-friendly identifier for agent portfolio
                            title: data.agent?.title || data.agent?.tagline || "Real Estate Advisor",
                            phone: data.agent?.phone || "(469) 750-3330",
                            email: data.agent?.email || "andrew@cloudmortgage.io",
                            // Use API photo URL with fallback, decode over-encoded S3 URLs
                            photo: decodeS3Url(data.agent?.photoUrl || data.agent?.photo) || "/andrew-nguyen-agent.jpg",
                            company: data.agent?.company,
                            brokerage: data.settings?.brokerage?.name || data.agent?.company || "Cloud Mortgage, Inc",
                            // Decode over-encoded S3 URLs for brokerage logo
                            brokerageLogo: decodeS3Url(data.settings?.brokerage?.logo_url || data.settings?.brokerage?.logoUrl || data.agent?.brokerageLogo)
                        },
                        settings: {
                            accentColor: data.settings?.accent_color || data.settings?.accentColor || "#d6b27f",
                            showPrice: data.settings?.show_price ?? data.settings?.showPrice ?? true,
                            showContactForm: data.settings?.show_contact_form ?? data.settings?.showContactForm ?? true,
                            showDownloadButton: true
                        },
                        photos: (data.photos || []).map((photo, index) => ({
                            id: photo.id,
                            original: photo.url,
                            processed: photo.url,
                            room: photo.room_type || photo.roomType || `Room ${index + 1}`,
                            style: photo.style || "Staged"
                        }))
                    };
                    console.log('[PropertyGalleryPage] Transformed gallery:', transformedGallery);
                    console.log('[PropertyGalleryPage] Transformed agent.photo:', transformedGallery.agent?.photo);
                    console.log('[PropertyGalleryPage] Transformed property.description:', transformedGallery.property?.description?.substring(0, 50));
                    setGallery(transformedGallery);
                    setLoading(false);
                    return;
                }

                // If API fails, try localStorage as fallback
                console.log('[PropertyGalleryPage] API call failed with status:', response.status);
                const errorText = await response.text();
                console.log('[PropertyGalleryPage] Error response:', errorText);
                const galleries = JSON.parse(localStorage.getItem("publishedGalleries") || "{}");
                const savedGallery = galleries[galleryId];

                if (savedGallery) {
                    // Decode any over-encoded URLs in localStorage data
                    if (savedGallery.agent) {
                        savedGallery.agent.photo = decodeS3Url(savedGallery.agent.photo);
                        savedGallery.agent.brokerageLogo = decodeS3Url(savedGallery.agent.brokerageLogo);
                    }
                    setGallery(savedGallery);
                } else {
                    // Only use sample data if both API and localStorage fail
                    console.warn("Gallery not found in API or localStorage, using sample data");
                    setGallery(SAMPLE_GALLERY);
                }
            } catch (error) {
                console.error("Error fetching gallery:", error);
                // Fallback to localStorage on network error
                const galleries = JSON.parse(localStorage.getItem("publishedGalleries") || "{}");
                const savedGallery = galleries[galleryId];

                if (savedGallery) {
                    // Decode any over-encoded URLs in localStorage data
                    if (savedGallery.agent) {
                        savedGallery.agent.photo = decodeS3Url(savedGallery.agent.photo);
                        savedGallery.agent.brokerageLogo = decodeS3Url(savedGallery.agent.brokerageLogo);
                    }
                    setGallery(savedGallery);
                } else {
                    setGallery(SAMPLE_GALLERY);
                }
            }
            setLoading(false);
        };
        fetchGallery();
    }, [galleryId]);

    useEffect(() => {
        if (gallery?.property?.address) {
            setRequestInfoForm((prev) => ({
                ...prev,
                message: `I am interested in ${gallery.property.address}`
            }));
        }
    }, [gallery]);

    useEffect(() => {
        if (!isSlideshowPlaying || !gallery) {
            return undefined;
        }

        const interval = setInterval(() => {
            setCurrentPhotoIndex((prev) => (
                prev === gallery.photos.length - 1 ? 0 : prev + 1
            ));
        }, 4000);

        return () => clearInterval(interval);
    }, [isSlideshowPlaying, gallery]);

    // Fetch other listings from the same agent
    useEffect(() => {
        const fetchAgentListings = async () => {
            // Need agent slug from the gallery response
            const agentSlug = gallery?.agent?.slug;
            if (!agentSlug) {
                console.log('[PropertyGalleryPage] No agent slug available, skipping agent listings fetch');
                return;
            }

            try {
                const apiUrl = process.env.REACT_APP_API_BASE || (
                    window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://api.cloudmortgage.io'
                );

                console.log('[PropertyGalleryPage] Fetching agent listings for:', agentSlug);
                const response = await fetch(`${apiUrl}/api/public/galleries/agent/${agentSlug}`);

                if (response.ok) {
                    const data = await response.json();
                    console.log('[PropertyGalleryPage] Agent listings received:', data.totalListings);
                    setAgentListings(data);
                } else {
                    console.log('[PropertyGalleryPage] Failed to fetch agent listings:', response.status);
                }
            } catch (error) {
                console.error('[PropertyGalleryPage] Error fetching agent listings:', error);
            }
        };

        if (gallery) {
            fetchAgentListings();
        }
    }, [gallery]);

    // Track page view for analytics (fire and forget)
    useEffect(() => {
        if (!galleryId || !gallery) return;

        const trackView = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_BASE || (
                    window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://api.cloudmortgage.io'
                );

                console.log('[PropertyGalleryPage] Tracking view for gallery:', galleryId);
                const response = await fetch(`${apiUrl}/api/public/galleries/${galleryId}/track`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ referrer: document.referrer || 'direct' })
                });

                if (response.ok) {
                    console.log('[PropertyGalleryPage] View tracked successfully');
                } else {
                    console.warn('[PropertyGalleryPage] Failed to track view:', response.status);
                }
            } catch (err) {
                console.warn('[PropertyGalleryPage] Error tracking view:', err);
            }
        };

        trackView();
    }, [galleryId, gallery]);

    const handlePrevPhoto = () => {
        setIsSlideshowPlaying(false);
        setCurrentPhotoIndex((prev) => (
            prev === 0 ? gallery.photos.length - 1 : prev - 1
        ));
    };

    const handleNextPhoto = () => {
        setIsSlideshowPlaying(false);
        setCurrentPhotoIndex((prev) => (
            prev === gallery.photos.length - 1 ? 0 : prev + 1
        ));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL || (
                window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://api.cloudmortgage.io'
            );
            const response = await fetch(`${API_URL}/api/public/galleries/${galleryId}/leads`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: contactForm.name,
                    email: contactForm.email,
                    phone: contactForm.phone || null,
                    message: contactForm.message || null,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to submit request');
            }

            setSubmitted(true);
            setContactForm({ name: '', email: '', phone: '', message: '' });
        } catch (error) {
            console.error('Failed to submit lead:', error);
            alert(error.message || 'Failed to submit your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequestInfoSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL || (
                window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://api.cloudmortgage.io'
            );
            const response = await fetch(`${API_URL}/api/public/galleries/${galleryId}/leads`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: requestInfoForm.name,
                    email: requestInfoForm.email,
                    phone: requestInfoForm.phone,
                    message: requestInfoForm.message,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to submit request');
            }

            // If user is interested in financing, also send to marketplace as a lead
            if (requestInfoForm.wantFinancing && gallery?.property) {
                try {
                    console.log('[PropertyGalleryPage] Gallery property for marketplace:', gallery.property);

                    // Parse property price - handle both number and string formats like "$580,000"
                    let propertyPrice = gallery.property.price;
                    if (typeof propertyPrice === 'string') {
                        propertyPrice = parseInt(propertyPrice.replace(/[^0-9]/g, ''), 10) || 0;
                    }
                    propertyPrice = propertyPrice || 0;

                    console.log('[PropertyGalleryPage] Property price:', propertyPrice, 'Type:', typeof propertyPrice);

                    // Calculate 5% down payment
                    const downPaymentAmount = Math.round(propertyPrice * 0.05);
                    const downPaymentPercent = '5%';

                    // Parse address for city/state if available
                    const addressParts = (gallery.property.address || '').split(',').map(s => s.trim());
                    const city = addressParts.length >= 2 ? addressParts[addressParts.length - 2] : '';
                    const stateZip = addressParts.length >= 1 ? addressParts[addressParts.length - 1] : '';
                    const state = stateZip.split(' ')[0] || '';
                    const zipCode = stateZip.split(' ')[1] || '';

                    // Clean phone number - must be at least 10 digits for marketplace API
                    const cleanPhone = requestInfoForm.phone.replace(/\D/g, '');
                    if (cleanPhone.length < 10) {
                        console.warn('[PropertyGalleryPage] Phone number too short for marketplace API, skipping marketplace submission');
                        throw new Error('Phone too short - skip marketplace');
                    }

                    const marketplacePayload = {
                        source: 'nhamy',
                        contact_name: requestInfoForm.name,
                        contact_email: requestInfoForm.email,
                        contact_phone: cleanPhone,
                        loan_details: {
                            occupancy: 'Primary Residence',
                            propertyType: 'Single Family',
                            loanPurpose: 'Purchase',
                            estimatedValue: propertyPrice || undefined,
                            downPayment: downPaymentAmount || undefined,
                            downPaymentPercent: downPaymentPercent,
                            city: city || undefined,
                            state: state || undefined,
                            zipCode: zipCode || undefined,
                            propertyAddress: gallery.property.address || undefined,
                            notes: `Interested in 5% down + $500 closing credit offer. Property: ${gallery.property.address || 'N/A'}. Price: $${propertyPrice?.toLocaleString() || 'N/A'}`,
                        },
                    };

                    console.log('[PropertyGalleryPage] Sending financing lead to marketplace:', marketplacePayload);

                    const marketplaceResponse = await fetch(`${API_URL}/api/rapidrates/leads`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(marketplacePayload),
                    });

                    if (marketplaceResponse.ok) {
                        console.log('[PropertyGalleryPage] Successfully sent financing lead to marketplace');
                    } else {
                        console.warn('[PropertyGalleryPage] Failed to send financing lead to marketplace:', await marketplaceResponse.text());
                    }
                } catch (marketplaceError) {
                    // Don't fail the main request if marketplace submission fails
                    console.error('[PropertyGalleryPage] Error sending to marketplace:', marketplaceError);
                }
            }

            setRequestInfoSubmitted(true);
            setTimeout(() => {
                setShowRequestInfoModal(false);
                setRequestInfoSubmitted(false);
                setRequestInfoForm({ name: '', phone: '', email: '', message: '', wantFinancing: false });
            }, 2000);
        } catch (error) {
            console.error('Failed to submit lead:', error);
            alert(error.message || 'Failed to submit your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleShare = async () => {
        try {
            if (navigator.share) {
                await navigator.share({
                    title: gallery?.property?.name || "Property Gallery",
                    url: window.location.href
                });
                return;
            }
            await navigator.clipboard.writeText(window.location.href);
        } catch (error) {
            if (error?.name !== "AbortError") {
                try {
                    await navigator.clipboard.writeText(window.location.href);
                } catch (clipboardError) {
                    console.error("Unable to share property gallery", clipboardError);
                }
            }
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0
        }).format(price);
    };

    const formatNumberValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "";
        }

        if (typeof value === "number" && Number.isFinite(value)) {
            return value.toLocaleString("en-US");
        }

        const normalizedValue = String(value).replace(/[^0-9.]/g, "");
        const parsedValue = Number(normalizedValue);

        if (!Number.isFinite(parsedValue)) {
            return String(value);
        }

        return parsedValue.toLocaleString("en-US");
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
                    <p className="text-white/60">Loading gallery...</p>
                </div>
            </div>
        );
    }

    if (!gallery || !gallery.photos || gallery.photos.length === 0) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="text-center">
                    <Home className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Gallery Not Found</h1>
                    <p className="text-white/60">This property gallery may have been removed or the link is invalid.</p>
                </div>
            </div>
        );
    }

    const currentPhoto = gallery.photos[currentPhotoIndex] || gallery.photos[0];
    const property = gallery.property || {};
    const agent = gallery.agent || {};
    const template = gallery.template || "luxury-dark";
    const theme = getPropertyGalleryTemplate(template);
    const accentColor = gallery.settings?.accentColor || theme.accentColor;
    const settings = gallery.settings || { showPrice: true, showContactForm: true };
    const isDarkTheme = theme.isDark;
    const headingStyle = { fontFamily: theme.headingFont };
    const bodyStyle = { fontFamily: theme.bodyFont };
    const stats = [
        property.bedrooms ? { icon: Bed, label: "Bedrooms", value: `${property.bedrooms}` } : null,
        property.bathrooms ? { icon: Bath, label: "Bathrooms", value: `${property.bathrooms}` } : null,
        property.squareFeet ? { icon: Square, label: "Interior", value: `${formatNumberValue(property.squareFeet)} sq ft` } : null
    ].filter(Boolean);

    const secondaryButtonClass = isDarkTheme
        ? "bg-white/10 hover:bg-white/18 text-white border border-white/10"
        : "bg-white/75 hover:bg-white text-slate-700 border border-white/70";

    const fieldClass = isDarkTheme
        ? "bg-white/8 border border-white/10 text-white placeholder-white/35 focus:border-white/30"
        : "bg-white/75 border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-slate-300";

    return (
        <>
            {/* OG Meta Tags for social sharing */}
            <GalleryMetaTags gallery={gallery} galleryId={galleryId} />

            <div className={`relative min-h-screen overflow-hidden ${theme.bg}`} style={bodyStyle}>
                <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${theme.shell}`} />
            <div
                className="pointer-events-none absolute -top-24 right-[-8rem] h-80 w-80 rounded-full blur-3xl opacity-20"
                style={{ backgroundColor: accentColor }}
            />
            <div
                className="pointer-events-none absolute top-[28rem] left-[-6rem] h-72 w-72 rounded-full blur-3xl opacity-10"
                style={{ backgroundColor: accentColor }}
            />

            <div className="relative">
                <section className="relative min-h-[88vh] overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentPhoto.id || currentPhotoIndex}
                            initial={{ scale: 1.02, opacity: 0.88 }}
                            animate={{ scale: 1.1, opacity: 1 }}
                            exit={{ opacity: 0.82 }}
                            transition={{ duration: 6.5, ease: "easeOut" }}
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${currentPhoto.processed})` }}
                        />
                    </AnimatePresence>

                    <div className={`pointer-events-none absolute inset-0 bg-gradient-to-b ${theme.heroOverlay}`} />
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.22),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(0,0,0,0.28),transparent_32%)]" />

                    <div className="absolute inset-x-0 top-0 z-20 p-4 sm:p-6">
                        <div className="mx-auto flex max-w-7xl items-center justify-between">
                            <div />
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowRequestInfoModal(true)}
                                    className="rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    style={{ backgroundColor: accentColor }}
                                >
                                    Request Info
                                </button>
                                <button
                                    onClick={handleShare}
                                    className={`rounded-full p-3 transition-colors backdrop-blur-md ${secondaryButtonClass}`}
                                    aria-label="Share property gallery"
                                >
                                    <Share2 className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 mx-auto flex min-h-[88vh] max-w-7xl items-end px-6 pb-28 pt-28">
                        <div className="w-full">
                            <motion.div
                                initial="hidden"
                                animate="visible"
                                className="max-w-3xl"
                            >
                                <motion.h1
                                    custom={0}
                                    variants={fadeUp}
                                    className={`max-w-2xl text-5xl font-semibold leading-[0.95] sm:text-6xl lg:text-7xl ${theme.textPrimary}`}
                                    style={headingStyle}
                                >
                                    {property.name || "Property Gallery"}
                                </motion.h1>
                                {property.address && (
                                    <motion.div
                                        custom={0.08}
                                        variants={fadeUp}
                                        className={`mt-5 flex items-center gap-2 text-base sm:text-lg ${theme.textSecondary}`}
                                    >
                                        <MapPin className="h-5 w-5" />
                                        <span>{property.address}</span>
                                    </motion.div>
                                )}
                                <motion.div
                                    custom={0.16}
                                    variants={fadeUp}
                                    className="mt-8 flex flex-wrap items-end gap-4"
                                >
                                    {settings.showPrice && property.price && (
                                        <div
                                            className="text-3xl font-semibold sm:text-5xl"
                                            style={{ ...headingStyle, color: accentColor, textShadow: isDarkTheme ? "0 10px 30px rgba(0,0,0,0.35)" : "none" }}
                                        >
                                            {formatPrice(property.price)}
                                        </div>
                                    )}
                                </motion.div>
                                {stats.length > 0 && (
                                    <motion.div
                                        custom={0.24}
                                        variants={fadeUp}
                                        className="mt-8 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3"
                                    >
                                        {stats.map((stat) => {
                                            const Icon = stat.icon;
                                            return (
                                                <div
                                                    key={stat.label}
                                                    className={`rounded-2xl border p-4 shadow-xl ${theme.subtlePanel} ${theme.cardBorder}`}
                                                >
                                                    <div className={`mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] ${theme.textMuted}`}>
                                                        <Icon className="h-4 w-4" />
                                                        <span>{stat.label}</span>
                                                    </div>
                                                    <div className={`text-lg font-semibold ${theme.textPrimary}`} style={headingStyle}>
                                                        {stat.value}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>
                    </div>

                    <button
                        onClick={handlePrevPhoto}
                        className="absolute left-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/25 text-white backdrop-blur-md transition-colors hover:bg-black/45"
                        aria-label="Previous photo"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        onClick={handleNextPhoto}
                        className="absolute right-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/25 text-white backdrop-blur-md transition-colors hover:bg-black/45"
                        aria-label="Next photo"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>

                    <div className="absolute bottom-6 left-0 right-0 z-20 px-6">
                        <div className="mx-auto flex max-w-7xl flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                            <div className="flex flex-wrap items-center gap-2">
                                {gallery.photos.slice(0, 10).map((photo, index) => (
                                    <button
                                        key={photo.id}
                                        onClick={() => {
                                            setIsSlideshowPlaying(false);
                                            setCurrentPhotoIndex(index);
                                        }}
                                        className={`relative h-14 w-20 overflow-hidden rounded-2xl border transition-all ${
                                            index === currentPhotoIndex
                                                ? "scale-[1.06] shadow-xl"
                                                : "opacity-75 hover:opacity-100"
                                        }`}
                                        style={{
                                            borderColor: index === currentPhotoIndex ? accentColor : "rgba(255,255,255,0.16)"
                                        }}
                                    >
                                        <img
                                            src={photo.processed}
                                            alt={photo.room || `Photo ${index + 1}`}
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                        <div className={`absolute inset-0 ${index === currentPhotoIndex ? "bg-transparent" : "bg-black/15"}`} />
                                    </button>
                                ))}
                                {gallery.photos.length > 10 && (
                                    <div
                                        className="flex h-14 w-20 items-center justify-center rounded-2xl border border-white/20 bg-black/30 backdrop-blur-sm text-white text-sm font-medium"
                                    >
                                        +{gallery.photos.length - 10}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 self-end">
                                <button
                                    onClick={() => setIsSlideshowPlaying(!isSlideshowPlaying)}
                                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${secondaryButtonClass}`}
                                >
                                    {isSlideshowPlaying ? (
                                        <>
                                            <Pause className="w-4 h-4" />
                                            Pause
                                        </>
                                    ) : (
                                        <>
                                            <Play className="w-4 h-4" />
                                            Slideshow
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative mx-auto max-w-7xl px-6 py-16">
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
                        <div className="space-y-8">
                            <div className={`rounded-[28px] border p-8 shadow-xl ${theme.cardBg} ${theme.cardBorder}`}>
                                <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                                    <div className="max-w-2xl">
                                        <h2 className={`text-3xl font-semibold ${theme.textPrimary}`} style={headingStyle}>
                                            About This Property
                                        </h2>
                                        {property.description && (
                                            <p className={`mt-4 text-base leading-8 ${theme.textSecondary}`}>
                                                {property.description}
                                            </p>
                                        )}
                                    </div>
                                    {settings.showPrice && property.price && (
                                        <div className={`rounded-3xl border px-6 py-5 ${theme.cardAltBg} ${theme.cardBorder}`}>
                                            <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${theme.textMuted}`}>Offered At</p>
                                            <p className="mt-2 text-3xl font-semibold" style={{ ...headingStyle, color: accentColor }}>
                                                {formatPrice(property.price)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {property.features && property.features.length > 0 && (
                                <div className={`rounded-[28px] border p-8 shadow-xl ${theme.cardBg} ${theme.cardBorder}`}>
                                    <h2 className={`text-2xl font-semibold ${theme.textPrimary}`} style={headingStyle}>
                                        Features
                                    </h2>
                                    <div className="mt-6 flex flex-wrap gap-3">
                                        {property.features.map((feature, index) => (
                                            <span
                                                key={index}
                                                className={`rounded-full px-4 py-2.5 text-sm font-medium shadow-sm ${theme.badgeClass}`}
                                            >
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className={`rounded-[28px] border p-8 shadow-xl ${theme.cardBg} ${theme.cardBorder}`}>
                                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                    <div>
                                        <h2 className={`text-2xl font-semibold ${theme.textPrimary}`} style={headingStyle}>
                                            All Photos
                                        </h2>
                                    </div>
                                    <p className={`text-sm ${theme.textSecondary}`}>
                                        Tap any image for the immersive view.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
                                    {gallery.photos.map((photo, index) => {
                                        const tileClass = index === 0
                                            ? "sm:col-span-2 lg:col-span-4 lg:row-span-2 aspect-[16/10]"
                                            : index === 1 || index === 2
                                            ? "aspect-[4/3] lg:col-span-2"
                                            : "aspect-[4/3] lg:col-span-2";

                                        return (
                                            <button
                                                key={photo.id}
                                                onClick={() => {
                                                    setIsSlideshowPlaying(false);
                                                    setCurrentPhotoIndex(index);
                                                    setShowLightbox(true);
                                                }}
                                                className={`group relative overflow-hidden rounded-[24px] ${tileClass} ${theme.galleryTile}`}
                                            >
                                                <img
                                                    src={photo.processed}
                                                    alt={photo.room || `Property photo ${index + 1}`}
                                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    loading={index < 2 ? "eager" : "lazy"}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                                                <div className="absolute bottom-3 right-3">
                                                    <div className="rounded-full border border-white/20 bg-black/30 px-3 py-1 text-xs text-white backdrop-blur-md">
                                                        {index + 1}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="lg:sticky lg:top-6 lg:self-start">
                            <div className="relative">
                                <div
                                    className="absolute inset-0 rounded-[32px] opacity-90 blur-xl"
                                    style={{ background: `linear-gradient(135deg, ${accentColor}55, transparent 55%)` }}
                                />
                                <div className={`relative rounded-[32px] border p-7 shadow-2xl ${theme.cardBg} ${theme.cardBorder}`}>
                                    {/* $500 Closing Credit Promotional Banner */}
                                    <div className={`mb-6 rounded-3xl border px-6 py-5 ${theme.cardAltBg} ${theme.cardBorder}`}>
                                        <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${theme.textMuted}`}>Special Offer</p>
                                        <p className="mt-2 text-lg font-semibold" style={{ color: accentColor }}>
                                            5% down + $500 credit
                                        </p>
                                        <p className={`mt-1 text-sm ${theme.textSecondary}`}>
                                            Available through our lending partner.
                                        </p>
                                    </div>

                                    {(agent.name || agent.photo) && (
                                        <div className="mb-6">
                                            <div className="flex items-center gap-4">
                                                {agent.photo ? (
                                                    <div
                                                        className="rounded-full p-[3px]"
                                                        style={{ background: `linear-gradient(135deg, ${accentColor}, rgba(255,255,255,0.55))` }}
                                                    >
                                                        <img
                                                            src={agent.photo}
                                                            alt={agent.name || "Agent"}
                                                            className="h-20 w-20 rounded-full object-cover"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg"
                                                        style={{ background: `linear-gradient(135deg, ${accentColor}, rgba(15,23,42,0.92))` }}
                                                    >
                                                        <User className="h-9 w-9" />
                                                    </div>
                                                )}
                                                <div>
                                                    {agent.name && (
                                                        <h3 className={`text-2xl font-semibold ${theme.textPrimary}`} style={headingStyle}>
                                                            {agent.name}
                                                        </h3>
                                                    )}
                                                    {agent.title && (
                                                        <p className={`mt-1 text-sm ${theme.textSecondary}`}>{agent.title}</p>
                                                    )}
                                                    {agent.brokerage && (
                                                        <p className="mt-2 text-sm font-medium" style={{ color: accentColor }}>
                                                            {agent.brokerage}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {agent.brokerageLogo && (
                                        <div className={`mb-6 rounded-2xl border p-4 ${theme.cardAltBg} ${theme.cardBorder}`}>
                                            <img
                                                src={agent.brokerageLogo}
                                                alt={agent.brokerage || "Brokerage"}
                                                className="h-10 object-contain"
                                            />
                                        </div>
                                    )}

                                    {/* Single Request Info button - captures leads for both realtor and marketplace */}
                                    <button
                                        onClick={() => setShowRequestInfoModal(true)}
                                        className="flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                        style={{ backgroundColor: accentColor }}
                                    >
                                        <MessageCircle className="h-5 w-5" />
                                        Request Info
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* More From Agent Section */}
                {agentListings && agentListings.totalListings > 1 && (
                    <MoreFromAgent
                        agent={agentListings.agent}
                        listings={agentListings.listings}
                        currentGalleryId={gallery.id}
                        accentColor={accentColor}
                    />
                )}

                <footer className={`border-t ${theme.sectionBorder} ${theme.footerBg}`}>
                    <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-8 sm:flex-row sm:items-center sm:justify-between">
                        <div className={`flex items-center gap-2 text-sm ${theme.textMuted}`}>
                            <span>Presented with</span>
                            <a
                                href="https://cloudmortgage.io/flow"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${theme.textSecondary} transition-opacity hover:opacity-80`}
                            >
                                Flow by Cloud Mortgage, Inc
                            </a>
                            <ExternalLink className="h-3.5 w-3.5" />
                        </div>
                        {settings.showDownloadButton && (
                            <button className={`flex items-center gap-2 self-start rounded-full px-4 py-2 text-sm font-medium transition-colors sm:self-auto ${secondaryButtonClass}`}>
                                <Download className="h-4 w-4" />
                                Download Photos
                            </button>
                        )}
                    </div>
                </footer>
            </div>

            <AnimatePresence>
                {showLightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                        onClick={() => setShowLightbox(false)}
                    >
                        <button
                            onClick={() => setShowLightbox(false)}
                            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                            aria-label="Close full screen gallery"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePrevPhoto();
                            }}
                            className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                            aria-label="Previous full screen photo"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNextPhoto();
                            }}
                            className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                            aria-label="Next full screen photo"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>

                        <div
                            className="flex h-full w-full items-center justify-center p-16"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={currentPhoto.processed}
                                alt={currentPhoto.room || "Property photo"}
                                className="max-h-full max-w-full rounded-2xl object-contain"
                            />
                        </div>

                        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-5 py-2 text-sm text-white backdrop-blur-md">
                            {currentPhotoIndex + 1} of {gallery.photos.length}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showRequestInfoModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm p-4"
                        onClick={() => setShowRequestInfoModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            className="w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                                <h2 className="text-xl font-semibold text-slate-900">Learn more about this property</h2>
                                <button
                                    onClick={() => setShowRequestInfoModal(false)}
                                    className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-slate-100"
                                    aria-label="Close request info form"
                                >
                                    <X className="h-5 w-5 text-slate-500" />
                                </button>
                            </div>

                            <div className="p-6">
                                {requestInfoSubmitted ? (
                                    <div className="py-8 text-center">
                                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                                            <Check className="h-8 w-8 text-emerald-600" />
                                        </div>
                                        <h3 className="mb-2 text-xl font-semibold text-slate-900">Request Sent</h3>
                                        <p className="text-slate-600">The agent will be in touch with you shortly.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleRequestInfoSubmit} className="space-y-4">
                                        <input
                                            type="text"
                                            placeholder="Name"
                                            value={requestInfoForm.name}
                                            onChange={(e) => setRequestInfoForm((prev) => ({ ...prev, name: e.target.value }))}
                                            required
                                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        />
                                        <input
                                            type="tel"
                                            placeholder="Phone"
                                            value={requestInfoForm.phone}
                                            onChange={(e) => setRequestInfoForm((prev) => ({ ...prev, phone: e.target.value }))}
                                            required
                                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        />
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={requestInfoForm.email}
                                            onChange={(e) => setRequestInfoForm((prev) => ({ ...prev, email: e.target.value }))}
                                            required
                                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        />
                                        <div>
                                            <label className="mb-1 block text-sm font-medium text-slate-700">Message</label>
                                            <textarea
                                                value={requestInfoForm.message}
                                                onChange={(e) => setRequestInfoForm((prev) => ({ ...prev, message: e.target.value }))}
                                                rows={3}
                                                className="w-full resize-none rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full rounded-2xl py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                                            style={{ backgroundColor: accentColor }}
                                        >
                                            {isSubmitting ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <Loader2 className="h-5 w-5 animate-spin" />
                                                    Sending...
                                                </span>
                                            ) : (
                                                "Request Info"
                                            )}
                                        </button>
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                id="financing"
                                                checked={requestInfoForm.wantFinancing}
                                                onChange={(e) => setRequestInfoForm((prev) => ({ ...prev, wantFinancing: e.target.checked }))}
                                                className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-400"
                                            />
                                            <label htmlFor="financing" className="text-sm text-slate-600">
                                                Tell me about financing with 5% down and the $500 closing credit
                                            </label>
                                        </div>
                                        <p className="text-xs leading-relaxed text-slate-500">
                                            By pressing Request Info, you agree that real estate professionals may contact you via phone or text about your inquiry. Consent is not required to purchase any property. Message and data rates may apply.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            </div>
        </>
    );
};

PropertyGalleryPage.propTypes = {
    galleryId: PropTypes.string.isRequired
};

export default PropertyGalleryPage;
